import { useCallback, useEffect, useRef, useState, type CSSProperties, type ReactNode } from 'react';

export interface ActionMenuItem {
  key: string;
  label: string;
  icon?: ReactNode;
  disabled?: boolean;
  danger?: boolean;
  onClick: () => void;
}

/**
 * Dropdown menu thao tác theo chuẩn LMS vnEdu (3 chấm mở menu thả xuống).
 * Sử dụng position: fixed dựa theo toạ độ button để không bị tràn / che bởi overflow của bảng.
 */
export function ActionMenu({
  items,
  ariaLabel = 'Thao tác',
}: {
  items: ActionMenuItem[];
  ariaLabel?: string;
}): ReactNode {
  const [open, setOpen] = useState(false);
  const [dropdownStyle, setDropdownStyle] = useState<CSSProperties>({});
  const triggerRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const updatePosition = useCallback(() => {
    if (!triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom;
    const openUp = spaceBelow < 210 && rect.top > 210;

    const style: CSSProperties = {
      position: 'fixed',
      right: `${window.innerWidth - rect.right}px`,
      zIndex: 99999,
    };

    if (openUp) {
      style.bottom = `${window.innerHeight - rect.top + 4}px`;
    } else {
      style.top = `${rect.bottom + 4}px`;
    }

    setDropdownStyle(style);
  }, []);

  const toggle = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!open) {
      updatePosition();
      setOpen(true);
    } else {
      setOpen(false);
    }
  };

  useEffect(() => {
    if (!open) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target as Node) &&
        triggerRef.current &&
        !triggerRef.current.contains(e.target as Node)
      ) {
        setOpen(false);
      }
    };
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setOpen(false);
      }
    };
    const handleScrollOrResize = () => {
      setOpen(false);
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleKeyDown);
    window.addEventListener('scroll', handleScrollOrResize, true);
    window.addEventListener('resize', handleScrollOrResize);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('scroll', handleScrollOrResize, true);
      window.removeEventListener('resize', handleScrollOrResize);
    };
  }, [open]);

  return (
    <div className="action-menu-container">
      <button
        ref={triggerRef}
        type="button"
        className={`btn-action-trigger ${open ? 'active' : ''}`}
        aria-label={ariaLabel}
        aria-haspopup="true"
        aria-expanded={open}
        onClick={toggle}
      >
        <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor">
          <circle cx="5" cy="12" r="2" />
          <circle cx="12" cy="12" r="2" />
          <circle cx="19" cy="12" r="2" />
        </svg>
      </button>

      {open && (
        <div
          ref={dropdownRef}
          className="action-menu-dropdown"
          style={dropdownStyle}
          role="menu"
        >
          {items.map((item) => (
            <button
              key={item.key}
              type="button"
              role="menuitem"
              className={`action-menu-item ${item.danger ? 'action-menu-item--danger' : ''}`}
              disabled={item.disabled}
              onClick={(e) => {
                e.stopPropagation();
                setOpen(false);
                item.onClick();
              }}
            >
              {item.icon && <span className="action-menu-icon">{item.icon}</span>}
              <span className="action-menu-text">{item.label}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
